import type { SchedulingWindowsSearchPage } from 'api/scheduling_windows/endpoints/searchSchedulingWindows';

import type { SchedulingWindowReference } from './EditSchedulingWindowReference';

// The wire format for a scheduling-window cross-reference is
// `zone_name|window_name`; the backend validates it with `/^[^|]+\|[^|]+$/` and
// resolves it to an id on write. Editable entities hold the object form so a
// reference picker can bind to it directly, and stringify only at the `toSave`
// boundary — so the parse lives here rather than in each entity.

/**
 * Parse a wire path into the object form, or `null` when there is nothing to
 * pick.
 *
 * Anything that is not two non-empty halves yields `null`, matching the shape the
 * backend itself accepts (`/^[^|]+\|[^|]+$/`). That also covers its fallback of
 * rendering the bare id as text when a join finds no window: such a value cannot
 * be shown in a reference picker, so the field reads as empty and a subsequent
 * save clears it. Effectively unreachable while the foreign keys hold — the join
 * only fails if the id does not resolve.
 */
export const parseSchedulingWindowPath = (path: string | null | undefined): SchedulingWindowReference | null => {
  if (path === null || path === undefined) return null;
  const [zone_name, name] = path.split('|');
  return zone_name !== undefined && zone_name !== '' && name !== undefined && name !== '' ? { zone_name, name } : null;
};

/** Project back to the wire path, or `null` when nothing is picked. */
export const formatSchedulingWindowPath = (window: SchedulingWindowReference | null): string | null =>
  window !== null ? `${window.zone_name}|${window.name}` : null;

/**
 * Keep the windows of a search page that have a path, and count them out of the total.
 *
 * A window with no zone (`scheduling_windows.zone_id` is nullable) has no path, so a path-keyed picker cannot offer it.
 * The total has to drop with it: the picker shows "load more" while it holds fewer rows than the total, so counting a
 * row it will never hold keeps that offer up after everything selectable is loaded. Each page starts at the first row
 * (paging grows the limit, not the offset), so the rows dropped from a page are every zone-less one it has seen: the
 * total is exact once the page is complete, and an upper bound before.
 */
export const withPathsOnly = ({ items, total }: SchedulingWindowsSearchPage): { readonly items: SchedulingWindowReference[]; readonly total: number } => {
  const kept = items.flatMap(({ zone_name, name }) => (zone_name !== null ? [{ zone_name, name }] : []));
  return { items: kept, total: total - (items.length - kept.length) };
};
